/**
 * API Hooks
 * Custom hooks for API calls and data fetching using React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import { apiService } from '../services/api';

import {
  LoginForm,
  RegisterForm,
  DevLoginForm,
  RestaurantProfileForm,
  InvoiceEditForm,
} from '../types/forms';
import type {
  ApiError,
  BagWasteEntryPayload,
  BagWasteResponse,
  ComplianceDocumentDetail,
  ComplianceDocumentsListParams,
  ComplianceDocumentsListResponse,
  ComplianceReminderFeedResponse,
  ConsumptionEntry,
  ManualConsumptionEntryPayload,
  ProgressScoreSeriesResponse,
  ProgressScoreSummaryResponse,
  UserFilesListParams,
  UserFilesListResponse,
  DomainShareScope,
  Goal,
  QrShareCreateRequest,
  QrShareDetail,
  QrShareListParams,
  QrShareListResponse,
  QrShareScopePreviewResponse,
  QrShareUpdateRequest,
} from '../types/api';
import { BaseFilters } from '../types/api';
import { useAuthStore } from '../stores/authStore';

// ============================================================================
// Query Keys
// ============================================================================

// Utility to sanitize filters for query keys to prevent circular reference errors
const sanitizeFiltersForQueryKey = (filters: BaseFilters) => {
  const sanitized: Record<string, string> = {};

  Object.entries(filters).forEach(([key, value]) => {
    // Only include primitive values that can be safely serialized
    if (typeof value === 'string' || typeof value === 'number') {
      sanitized[key] = String(value);
    }
  });

  return sanitized;
};

const sanitizeComplianceFiltersForQueryKey = (
  filters: ComplianceDocumentsListParams,
) => {
  const sanitized: Record<string, string> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        const sorted = [...value].map(String).sort();
        sanitized[key] = sorted.join(',');
      }
      return;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      sanitized[key] = String(value);
    }
  });

  return sanitized;
};

interface ProgressScoreSeriesParams {
  start_month: string;
  end_month: string;
}

const INVOICES_KEY = ['invoices'] as const;
const UNIFIED_TRANSACTIONS_KEY = ['unified-transactions'] as const;
const CONSUMPTION_KEY = ['consumption'] as const;
const ANALYTICS_KEY = ['analytics'] as const;
const SUPPLIERS_KEY = ['suppliers'] as const;
const COMPLIANCE_KEY = ['compliance'] as const;
const SMART_SORTING_KEY = ['smart-sorting'] as const;
const USER_FILES_KEY = ['user-files'] as const;
const QR_SHARES_KEY = ['qr-shares'] as const;
const GOALS_KEY = ['goals'] as const;

export const queryKeys = {
  auth: {
    user: ['auth', 'user'] as const,
    profile: ['auth', 'profile'] as const,
  },
  dashboard: {
    summary: ['dashboard', 'summary'] as const,
  },
  goals: {
    all: GOALS_KEY,
  },
  invoices: {
    all: INVOICES_KEY,
    lists: () => [...INVOICES_KEY, 'list'] as const,
    list: (filters: BaseFilters) =>
      [...INVOICES_KEY, 'list', sanitizeFiltersForQueryKey(filters)] as const,
    details: () => [...INVOICES_KEY, 'detail'] as const,
    detail: (id: string | number) => [...INVOICES_KEY, 'detail', id] as const,
  },
  unifiedTransactions: {
    all: UNIFIED_TRANSACTIONS_KEY,
    list: (filters: BaseFilters) =>
      [
        ...UNIFIED_TRANSACTIONS_KEY,
        sanitizeFiltersForQueryKey(filters),
      ] as const,
    details: () => [...UNIFIED_TRANSACTIONS_KEY, 'detail'] as const,
    detail: (id: string | number, type: string) =>
      [...UNIFIED_TRANSACTIONS_KEY, 'detail', id, type] as const,
  },
  consumption: {
    all: CONSUMPTION_KEY,
    entries: (filters: BaseFilters) =>
      [
        ...CONSUMPTION_KEY,
        'entries',
        sanitizeFiltersForQueryKey(filters),
      ] as const,
  },
  analytics: {
    all: ANALYTICS_KEY,
    summary: ['analytics-summary'] as const,
    transactions: (filters?: any) =>
      ['analytics-transactions', filters] as const,
    transactionsSummary: (filters?: any) =>
      ['analytics-transactions-summary', filters] as const,
    charts: (filters?: any) => ['analytics-charts', filters] as const,
    progressScoreSeries: (params: ProgressScoreSeriesParams) =>
      [
        ...ANALYTICS_KEY,
        'progress-score-series',
        params.start_month,
        params.end_month,
      ] as const,
    progressScoreSummary: [...ANALYTICS_KEY, 'progress-score-summary'] as const,
  },
  suppliers: {
    all: SUPPLIERS_KEY,
    detail: (id: string | number) => [...SUPPLIERS_KEY, id] as const,
  },
  compliance: {
    all: COMPLIANCE_KEY,
    list: (filters: ComplianceDocumentsListParams) =>
      [
        ...COMPLIANCE_KEY,
        'list',
        sanitizeComplianceFiltersForQueryKey(filters),
      ] as const,
    detail: (id: string) => [...COMPLIANCE_KEY, 'detail', id] as const,
    reminders: [...COMPLIANCE_KEY, 'reminders'] as const,
  },
  smartSorting: {
    all: SMART_SORTING_KEY,
    summary: [...SMART_SORTING_KEY, 'summary'] as const,
    files: (params?: Record<string, string>) =>
      [...SMART_SORTING_KEY, 'files', params ?? {}] as const,
  },
  userFiles: {
    all: USER_FILES_KEY,
    list: (params: UserFilesListParams) =>
      [
        ...USER_FILES_KEY,
        'list',
        params.page,
        params.per_page,
        params.kind ?? '',
        params.search ?? '',
      ] as const,
  },
  qrShares: {
    all: QR_SHARES_KEY,
    list: (params: QrShareListParams) =>
      [
        ...QR_SHARES_KEY,
        'list',
        params.status ?? '',
        params.share_scope ?? '',
      ] as const,
    detail: (shareId: string) => [...QR_SHARES_KEY, 'detail', shareId] as const,
    scopePreview: (scope: DomainShareScope) =>
      [...QR_SHARES_KEY, 'scope-preview', scope] as const,
  },
} as const;

// ============================================================================
// Authentication Hooks
// ============================================================================

/**
 * Hook for user authentication operations
 */
export function useAuthOperations() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);
  const setProfile = useAuthStore((state) => state.setProfile);
  const setLoading = useAuthStore((state) => state.setLoading);
  const setError = useAuthStore((state) => state.setError);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: (data: LoginForm) => apiService.login(data),
    onMutate: () => {
      setLoading(true);
      setError(null);
    },
    onSuccess: async (response) => {
      const user = response.data.user;
      setUser(user);
      queryClient.setQueryData(queryKeys.auth.user, user);

      try {
        const profile = await apiService.getRestaurantProfile();
        setProfile(profile);
        queryClient.setQueryData(queryKeys.auth.profile, profile);
      } catch (error) {
        const apiError = error as ApiError & { status?: number };
        if (apiError?.status === 404) {
          setProfile(null);
          queryClient.removeQueries({ queryKey: queryKeys.auth.profile });
        } else {
          setProfile(null);
          throw error;
        }
      }
    },
    onError: (error) => {
      setError(error instanceof Error ? error.message : 'Login failed');
      setProfile(null);
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (data: RegisterForm) => apiService.register(data),
  });

  // Dev login mutation
  const devLoginMutation = useMutation({
    mutationFn: (data: DevLoginForm) => apiService.devLogin(data),
    onSuccess: (response) => {
      const user = response.data.user;
      setUser(user);
      queryClient.setQueryData(queryKeys.auth.user, user);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: () => apiService.logout(),
    onSuccess: () => {
      setUser(null);
      setProfile(null);
      queryClient.clear();
    },
  });

  // Password reset mutation
  const resetPasswordMutation = useMutation({
    mutationFn: (data: { token: string; new_password: string }) =>
      apiService.resetPassword(data),
  });

  return {
    login: loginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    devLogin: devLoginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    resetPassword: resetPasswordMutation.mutateAsync,
    isLoading:
      loginMutation.isPending ||
      registerMutation.isPending ||
      devLoginMutation.isPending ||
      logoutMutation.isPending ||
      resetPasswordMutation.isPending,
  };
}

// ============================================================================
// Profile Hooks
// ============================================================================

/**
 * Hook for restaurant profile operations
 */
export function useProfileOperations() {
  const queryClient = useQueryClient();
  const setProfile = useAuthStore((state) => state.setProfile);

  // Create profile mutation
  const createProfileMutation = useMutation({
    mutationFn: (data: RestaurantProfileForm) =>
      apiService.createRestaurantProfile(data),
    onSuccess: (response) => {
      setProfile(response.profile);
      queryClient.setQueryData(queryKeys.auth.profile, response.profile);
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: Partial<RestaurantProfileForm>) =>
      apiService.updateRestaurantProfile(data),
    onSuccess: (profile) => {
      setProfile(profile);
      queryClient.setQueryData(queryKeys.auth.profile, profile);
    },
  });

  return {
    createProfile: createProfileMutation.mutateAsync,
    updateProfile: updateProfileMutation.mutateAsync,
    isLoading:
      createProfileMutation.isPending || updateProfileMutation.isPending,
  };
}

// ============================================================================
// Dashboard Hooks
// ============================================================================

/**
 * Hook for dashboard data
 */
export function useDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard.summary,
    queryFn: () => apiService.getDashboardSummary(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

// ============================================================================
// Invoice Hooks
// ============================================================================

/**
 * Hook for invoice operations
 */
export function useInvoiceOperations() {
  const queryClient = useQueryClient();

  // Scan invoice mutation
  const scanInvoiceMutation = useMutation({
    mutationFn: (formData: FormData) => apiService.scanInvoice(formData),
    onSuccess: (_result) => {
      // Invalidate and refetch all affected queries
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      // Force refetch dashboard data immediately (bypass stale time)
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.consumption.all });
    },
  });

  // Update invoice mutation
  const updateInvoiceMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string | number;
      data: InvoiceEditForm;
    }) => apiService.updateInvoice(id, data),
    onSuccess: (updatedInvoice) => {
      // Update the specific invoice in cache immediately
      queryClient.setQueryData(
        queryKeys.invoices.detail(updatedInvoice.id),
        updatedInvoice,
      );

      // Invalidate all invoice-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      // Force refetch dashboard data immediately (bypass stale time)
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.consumption.all });

      // Also refetch the current invoice to ensure UI is in sync
      queryClient.refetchQueries({
        queryKey: queryKeys.invoices.detail(updatedInvoice.id),
        exact: true,
      });
    },
  });

  // Delete invoice mutation
  const deleteInvoiceMutation = useMutation({
    mutationFn: (id: string | number) => apiService.deleteInvoice(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: queryKeys.invoices.detail(deletedId),
      });
      // Invalidate all affected queries
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      // Force refetch dashboard data immediately (bypass stale time)
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.consumption.all });
    },
  });

  return {
    scanInvoice: scanInvoiceMutation.mutateAsync,
    updateInvoice: updateInvoiceMutation.mutateAsync,
    deleteInvoice: deleteInvoiceMutation.mutateAsync,
    isLoading:
      scanInvoiceMutation.isPending ||
      updateInvoiceMutation.isPending ||
      deleteInvoiceMutation.isPending,
  };
}

/**
 * Hook for sales document operations (scan and confirm)
 */
export function useSalesDocumentOperations() {
  const queryClient = useQueryClient();

  // Mutation for scanning sales documents (returns parsed data for confirmation)
  const scanSalesDocument = useMutation({
    mutationFn: (formData: FormData) => apiService.scanSalesDocument(formData),
    onSuccess: () => {
      // Invalidate covers volumes queries after successful scan
      queryClient.invalidateQueries({ queryKey: ['covers-volumes'] });
      // Force refetch dashboard data immediately (bypass stale time)
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      // Invalidate analytics data since sales data affects charts and calculations
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
    },
    onError: (error) => {
      console.error('Error scanning sales document:', error);
    },
  });

  // Mutation for confirming sales documents (saves to database)
  const confirmSalesDocument = useMutation({
    mutationFn: (
      confirmationData: import('../types/api').SalesDocumentConfirmation,
    ) => apiService.confirmSalesDocument(confirmationData),
    onSuccess: () => {
      // Invalidate covers volumes queries after successful confirmation
      queryClient.invalidateQueries({ queryKey: ['covers-volumes'] });
      // Force refetch dashboard data immediately (bypass stale time)
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      // Invalidate analytics data since sales data affects charts and calculations
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
    },
    onError: (error) => {
      console.error('Error confirming sales document:', error);
    },
  });

  return {
    scanSalesDocument: scanSalesDocument.mutateAsync,
    confirmSalesDocument: confirmSalesDocument.mutateAsync,
    isLoading: scanSalesDocument.isPending || confirmSalesDocument.isPending,
    scanLoading: scanSalesDocument.isPending,
    confirmLoading: confirmSalesDocument.isPending,
  };
}

/**
 * Hook for fetching invoices with filters
 */
export function useInvoices(filters?: {
  page?: number;
  per_page?: number;
  service_type?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: queryKeys.invoices.list(filters || {}),
    queryFn: () => apiService.getInvoices(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook for fetching unified transactions (OCR, manual, sales) with filters
 */
export function useUnifiedTransactions(filters?: {
  page?: number;
  per_page?: number;
  service_type?: string;
  transaction_type?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}) {
  return useQuery({
    queryKey: queryKeys.unifiedTransactions.list(filters || {}),
    queryFn: () => apiService.getUnifiedTransactions(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook for fetching compliance documents with filters
 */
export function useComplianceDocuments(
  filters: ComplianceDocumentsListParams = {},
) {
  return useQuery<ComplianceDocumentsListResponse>({
    queryKey: queryKeys.compliance.list(filters),
    queryFn: () => apiService.getComplianceDocuments(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnMount: 'always',
  });
}

export function useUserFiles(params: UserFilesListParams) {
  return useQuery<UserFilesListResponse>({
    queryKey: queryKeys.userFiles.list(params),
    queryFn: () => apiService.getUserFiles(params),
    staleTime: 60_000,
    refetchOnMount: 'always',
  });
}

// ============================================================================
// QR sharing Hooks
// ============================================================================

/**
 * Counts the files a scope would include. Never cached: the snapshot is taken at
 * creation time, so a stale count would misrepresent what is about to be published.
 */
export function useQrShareScopePreview(scope: DomainShareScope | null) {
  return useQuery<QrShareScopePreviewResponse>({
    queryKey: queryKeys.qrShares.scopePreview(scope ?? 'all_compliance'),
    queryFn: () => {
      if (!scope) throw new Error('Scope is required');
      return apiService.previewQrShareScope(scope);
    },
    enabled: scope !== null,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useQrShares(params: QrShareListParams = {}) {
  return useQuery<QrShareListResponse>({
    queryKey: queryKeys.qrShares.list(params),
    queryFn: () => apiService.getQrShares(params),
    staleTime: 30_000,
    refetchOnMount: 'always',
  });
}

export function useCreateQrShare() {
  const queryClient = useQueryClient();

  return useMutation<QrShareDetail, Error, QrShareCreateRequest>({
    mutationFn: (payload) => apiService.createQrShare(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.qrShares.all });
    },
  });
}

export function useQrShare(shareId: string | null) {
  return useQuery<QrShareDetail>({
    queryKey: queryKeys.qrShares.detail(shareId ?? 'unknown'),
    queryFn: () => {
      if (!shareId) throw new Error('Share id is required');
      return apiService.getQrShare(shareId);
    },
    enabled: shareId !== null,
    staleTime: 0,
  });
}

export function useUpdateQrShare() {
  const queryClient = useQueryClient();

  return useMutation<
    QrShareDetail,
    Error,
    { shareId: string; payload: QrShareUpdateRequest }
  >({
    mutationFn: ({ shareId, payload }) =>
      apiService.updateQrShare(shareId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.qrShares.all });
    },
  });
}

export function useArchiveQrShare() {
  const queryClient = useQueryClient();

  return useMutation<QrShareDetail, Error, string>({
    mutationFn: (shareId) => apiService.archiveQrShare(shareId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.qrShares.all });
    },
  });
}

export function useRevokeQrShare() {
  const queryClient = useQueryClient();

  return useMutation<QrShareDetail, Error, string>({
    mutationFn: (shareId) => apiService.revokeQrShare(shareId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.qrShares.all });
    },
  });
}

export function useRotateQrShareToken() {
  const queryClient = useQueryClient();

  return useMutation<QrShareDetail, Error, string>({
    mutationFn: (shareId) => apiService.rotateQrShareToken(shareId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.qrShares.all });
    },
  });
}

/**
 * Hook for fetching a single compliance document
 */
export function useComplianceDocument(documentId: string | null | undefined) {
  return useQuery<ComplianceDocumentDetail>({
    queryKey: queryKeys.compliance.detail(documentId ?? 'unknown'),
    queryFn: async () => {
      if (!documentId) {
        throw new Error('Cannot fetch compliance document without id');
      }
      return apiService.getComplianceDocument(documentId);
    },
    enabled: Boolean(documentId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: 'always',
  });
}

/**
 * Hook for fetching compliance reminders
 */
export function useComplianceReminders(options?: { enabled?: boolean }) {
  return useQuery<ComplianceReminderFeedResponse>({
    queryKey: queryKeys.compliance.reminders,
    queryFn: () => apiService.getComplianceReminders(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook for fetching a single invoice
 */
export function useInvoice(id: string | number) {
  return useQuery({
    queryKey: queryKeys.invoices.detail(id),
    queryFn: () => apiService.getInvoice(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Extract an HTTP status code from an unknown (axios-style) error, if present.
 */
function getHttpStatusFromError(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined;
  }
  const response = (error as { response?: unknown }).response;
  if (
    typeof response !== 'object' ||
    response === null ||
    !('status' in response)
  ) {
    return undefined;
  }
  const status = (response as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Retry transient failures (e.g. a freshly created record that briefly 404s
 * due to processing/replication timing) a few times, but never retry on
 * auth/permission errors.
 */
function retryTransientNotFound(failureCount: number, error: Error): boolean {
  const status = getHttpStatusFromError(error);
  if (status === 401 || status === 403) {
    return false;
  }
  return failureCount < 3;
}

/**
 * Hook for inferring transaction type from ID alone
 */
export function useTransactionType(id: string | number | null) {
  return useQuery({
    queryKey: ['transactionType', id],
    queryFn: () => apiService.getTransactionType(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Retry transient misses (record may not be queryable immediately after
    // processing completes), but bail out quickly on permission errors.
    retry: retryTransientNotFound,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
  });
}

/**
 * Hook for fetching unified transaction details (OCR, manual, sales)
 */
export function useTransactionDetails(
  id: string | number | null,
  transactionType: 'ocr' | 'manual' | 'sales_ocr' | 'sales_manual' | null,
) {
  return useQuery({
    queryKey: queryKeys.unifiedTransactions.detail(id!, transactionType!),
    queryFn: () => apiService.getTransactionDetails(id!, transactionType!),
    enabled: !!(id && transactionType),
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Avoid stranding users on the error state (which routes back to upload)
    // when the record simply isn't visible yet right after processing.
    retry: retryTransientNotFound,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
  });
}

/**
 * Hook for transaction operations (unified for OCR and manual)
 */
export function useTransactionOperations() {
  const queryClient = useQueryClient();

  // Update transaction mutation
  const updateTransactionMutation = useMutation({
    mutationFn: ({
      id,
      transactionType,
      data,
    }: {
      id: string | number;
      transactionType: 'ocr' | 'manual';
      data: InvoiceEditForm;
    }) => apiService.updateTransactionDetails(id, transactionType, data),
    onSuccess: (updatedTransaction, { id, transactionType }) => {
      // Update the specific transaction in cache immediately
      queryClient.setQueryData(
        queryKeys.unifiedTransactions.detail(id, transactionType),
        updatedTransaction,
      );

      // Invalidate all transaction-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.unifiedTransactions.all,
      });
      // Force refetch dashboard data immediately (bypass stale time)
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.consumption.all });

      // Also refetch the current transaction to ensure UI is in sync
      queryClient.refetchQueries({
        queryKey: queryKeys.unifiedTransactions.detail(id, transactionType),
        exact: true,
      });
    },
  });

  // Delete transaction mutation
  const deleteTransactionMutation = useMutation({
    mutationFn: ({
      id,
      transactionType,
    }: {
      id: string | number;
      transactionType: 'ocr' | 'manual' | 'sales_ocr' | 'sales_manual';
    }) => apiService.deleteTransaction(id, transactionType),
    onSuccess: (_, { id, transactionType }) => {
      // Remove the specific transaction from cache
      queryClient.removeQueries({
        queryKey: queryKeys.unifiedTransactions.detail(id, transactionType),
        exact: true,
      });

      // Invalidate all transaction-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.unifiedTransactions.all,
      });
      // Force refetch dashboard data immediately (bypass stale time)
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.consumption.all });
    },
  });

  return {
    updateTransaction: updateTransactionMutation.mutateAsync,
    deleteTransaction: deleteTransactionMutation.mutateAsync,
    isLoading:
      updateTransactionMutation.isPending ||
      deleteTransactionMutation.isPending,
  };
}

// ============================================================================
// Manual Entry Hooks
// ============================================================================

type BagWasteMutationInput = BagWasteEntryPayload & {
  consumption_unit: 'bolsas';
};

type ManualEntryMutationInput =
  | ManualConsumptionEntryPayload
  | BagWasteMutationInput;

const mapBagWasteResponseToConsumptionEntry = (
  response: BagWasteResponse,
): ConsumptionEntry => {
  const entry: ConsumptionEntry = {
    id: String(response.id),
    service_type: response.service_type,
    consumption: response.consumption,
    unit: response.consumption_unit,
    start_date: response.start_date,
    end_date: response.end_date,
    created_at: response.created_at,
    bags_value: response.bags,
    bag_size: response.bag_size,
    conversion_factor: response.conversion_factor,
  };

  if (response.cost_total != null) {
    entry.cost = response.cost_total;
  }
  if (response.currency) {
    entry.currency = response.currency;
  }
  if (response.notes) {
    entry.notes = response.notes;
  }

  return entry;
};

/**
 * Hook for manual entry operations
 */
export function useManualEntryOperations() {
  const queryClient = useQueryClient();

  // Log manual entry mutation
  const logEntryMutation = useMutation<
    ConsumptionEntry[],
    Error,
    ManualEntryMutationInput
  >({
    mutationFn: async (data) => {
      if ('bags' in data && data.consumption_unit === 'bolsas') {
        const { consumption_unit: _consumptionUnit, ...payload } = data;
        void _consumptionUnit;
        const response = await apiService.logBagWasteEntry(payload);
        return [mapBagWasteResponseToConsumptionEntry(response)];
      }
      return apiService.logManualEntry(data);
    },
    onSuccess: () => {
      // Invalidate all affected queries - manual entries create invoices too
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      // Force refetch dashboard data immediately (bypass stale time)
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      queryClient.invalidateQueries({ queryKey: queryKeys.consumption.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
    },
  });

  return {
    logEntry: logEntryMutation.mutateAsync,
    isLoading: logEntryMutation.isPending,
  };
}

/**
 * Hook for fetching consumption entries
 */
export function useConsumptionEntries(filters?: {
  service_type?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}) {
  return useQuery({
    queryKey: queryKeys.consumption.entries(filters || {}),
    queryFn: () => apiService.getConsumptionEntries(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ============================================================================
// Search and Filter Hooks
// ============================================================================

/**
 * Hook for search functionality with debouncing
 */
export function useSearch<T>(
  searchFunction: (query: string) => Promise<T[]>,
  _debounceDelay = 300,
) {
  const queryClient = useQueryClient();

  const searchMutation = useMutation({
    mutationFn: searchFunction,
    onSuccess: (results) => {
      // Cache search results
      queryClient.setQueryData(['search', 'results'], results);
    },
  });

  const debouncedSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        queryClient.setQueryData(['search', 'results'], []);
        return Promise.resolve([]);
      }
      return searchMutation.mutateAsync(query);
    },
    [searchMutation, queryClient],
  );

  return {
    search: debouncedSearch,
    results: queryClient.getQueryData(['search', 'results']) as T[] | undefined,
    isSearching: searchMutation.isPending,
    error: searchMutation.error,
  };
}

// ============================================================================
// File Upload Hooks
// ============================================================================

/**
 * Hook for file upload with progress tracking
 */
export function useFileUpload() {
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: ({
      endpoint,
      file,
      onProgress,
      additionalFields,
    }: {
      endpoint: string;
      file: File;
      onProgress?: (progress: number) => void;
      additionalFields?: Record<string, string | number>;
    }) =>
      apiService.uploadFileWithProgress({
        endpoint,
        file,
        ...(onProgress ? { onProgress } : {}),
        ...(additionalFields ? { additionalFields } : {}),
      }),
    onSuccess: () => {
      // Invalidate all affected queries
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.lists() });
      // Force refetch dashboard data immediately (bypass stale time)
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
      // Invalidate covers volumes in case file uploads include sales documents
      queryClient.invalidateQueries({ queryKey: ['covers-volumes'] });
    },
  });

  return {
    upload: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
    error: uploadMutation.error,
    progress: 0, // This would need to be tracked separately if needed
  };
}

// ============================================================================
// Generic API Hook
// ============================================================================

/**
 * Generic hook for making arbitrary API calls
 * Provides a unified interface for API requests with proper error handling
 */
export function useApi() {
  const apiCall = React.useCallback(
    async <T = unknown>(
      endpoint: string,
      options: {
        method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
        body?: any;
        params?: Record<string, any>;
        headers?: Record<string, string>;
      } = {},
    ): Promise<T> => {
      try {
        const { method = 'GET', body, params, headers } = options;

        const config: any = {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
        };

        if (body && method !== 'GET') {
          config.data = body;
        }

        if (params) {
          config.params = params;
        }

        const response = await apiService.request<T>({
          url: endpoint,
          ...config,
        });

        return response.data;
      } catch (error) {
        console.error(`API call failed for ${endpoint}:`, error);
        throw error;
      }
    },
    [],
  );

  return {
    apiCall,
  };
}

// ============================================================================
// Analytics Chart Hooks
// ============================================================================

/**
 * Hook for fetching analytics chart data
 */
export function useAnalyticsCharts(filters?: any) {
  return useQuery({
    queryKey: queryKeys.analytics.charts(filters),
    queryFn: () => apiService.getAnalyticsCharts(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useGoals() {
  return useQuery<Goal[]>({
    queryKey: queryKeys.goals.all,
    queryFn: () => apiService.getGoals(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTotalConsumptionChart(filters?: any) {
  return useQuery({
    queryKey: [
      'analytics',
      'total-consumption-chart',
      sanitizeFiltersForQueryKey(filters || {}),
    ],
    queryFn: () => apiService.getTotalConsumptionChart(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

export function useProgressScoreSeries(params: ProgressScoreSeriesParams) {
  return useQuery<ProgressScoreSeriesResponse>({
    queryKey: queryKeys.analytics.progressScoreSeries(params),
    queryFn: () => apiService.getProgressScoreSeries(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: Boolean(params?.start_month && params?.end_month),
  });
}

export function useProgressScoreSummary() {
  return useQuery<ProgressScoreSummaryResponse>({
    queryKey: queryKeys.analytics.progressScoreSummary,
    queryFn: () => apiService.getProgressScoreSummary(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook for fetching calendarized consumption data
 */
export function useCalendarizedConsumption(params: {
  start_month: string; // Format: YYYY-MM
  end_month: string; // Format: YYYY-MM
}) {
  return useQuery({
    queryKey: [
      'reports',
      'calendarized-consumption',
      params.start_month,
      params.end_month,
    ],
    queryFn: () => apiService.getCalendarizedConsumption(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!(params.start_month && params.end_month),
  });
}

/**
 * Hook for fetching calendarized CO2e data
 */
export function useCalendarizedCO2e(params: {
  start_month: string;
  end_month: string;
}) {
  return useQuery({
    queryKey: ['calendarized-co2e', params.start_month, params.end_month],
    queryFn: () => apiService.getCalendarizedCO2e(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!(params.start_month && params.end_month),
  });
}

/**
 * Hook for fetching calendarized CO2e data by scope
 */
export function useCalendarizedEmissionsByScope(params: {
  start_month: string;
  end_month: string;
}) {
  return useQuery({
    queryKey: [
      'calendarized-emissions-by-scope',
      params.start_month,
      params.end_month,
    ],
    queryFn: () => apiService.getCalendarizedEmissionsByScope(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!(params.start_month && params.end_month),
  });
}

/**
 * Hook for fetching covers volumes (sales data)
 */
export function useCoversVolumes() {
  return useQuery({
    queryKey: ['covers-volumes'],
    queryFn: () => apiService.getCoversVolumes(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook for covers volume operations (CRUD operations)
 */
export function useCoversVolumeOperations() {
  const queryClient = useQueryClient();

  // Update covers volume mutation
  const updateCoversVolumeMutation = useMutation({
    mutationFn: ({
      volumeId,
      data,
    }: {
      volumeId: number;
      data: import('../types/api').CoversVolumeUpdate;
    }) => apiService.updateCoversVolume(volumeId, data),
    onSuccess: (updatedVolume, { volumeId }) => {
      // Update the specific covers volume in cache immediately
      queryClient.setQueryData(['covers-volume', volumeId], updatedVolume);

      // Invalidate all covers volume-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['covers-volumes'] });
      // Force refetch dashboard data immediately (bypass stale time)
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });

      // Also refetch the current covers volume to ensure UI is in sync
      queryClient.refetchQueries({
        queryKey: ['covers-volume', volumeId],
        exact: true,
      });
    },
  });

  // Delete covers volume mutation
  const deleteCoversVolumeMutation = useMutation({
    mutationFn: (volumeId: number) => apiService.deleteCoversVolume(volumeId),
    onSuccess: (_, deletedVolumeId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: ['covers-volume', deletedVolumeId],
      });
      // Invalidate all affected queries
      queryClient.invalidateQueries({ queryKey: ['covers-volumes'] });
      // Force refetch dashboard data immediately (bypass stale time)
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
    },
  });

  return {
    updateCoversVolume: updateCoversVolumeMutation.mutateAsync,
    deleteCoversVolume: deleteCoversVolumeMutation.mutateAsync,
    isLoading:
      updateCoversVolumeMutation.isPending ||
      deleteCoversVolumeMutation.isPending,
  };
}
